import React, { useEffect, useState } from 'react';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import handleChange from '../../utils/formUtils/handleChange';
import FormField from '../common/FormField';
import {
  ComponentPlaceholderDTO,
  MemberDTO,
  MemberTypeDTO,
  ReplyTemplateDTO,
} from '../../services/apiClient';
import { useReplyTemplateApi } from '../../hooks/useReplyTemplateApi';
import { useComponentById } from '../../hooks/useComponentApi';
import Button from '../common/Button';
import Table from '../common/Table';
import { ComponentConstants } from '../../constants/ComponentConstants';
import { CourseStatusConstants } from '../../constants/CourseStatusConstants';
import { useMemberTypes } from '../../hooks/useMemberTypeApi';
import { useMemberCategories } from '../../hooks/useMemberCategoryApi';
import { formatDateForInput } from '../../utils/dateUtils/formatDateForInput';
import { formatDateTimeForInput } from '../../utils/dateUtils/formatDateTimeForInput';
import { RplStatusConstants } from '../../constants/RplStatusConstants';

interface BaseFeedbackComponent {
  id?: string;
  member?: MemberDTO;
  replyTemplateId?: string;
  feedback?: string;
}

interface FeedbackFormProps<T extends BaseFeedbackComponent> {
  componentId: string;
  recordId: string;
  componentStatuses: Array<{
    value?: string;
    label?: string | React.ReactNode;
  }>;
  statusFieldName: keyof T;
  defaultStatusId?: string;
  initialData?: T;
  onSubmit?: (data: T) => void;
  onCancel?: () => void;
  onChange?: (data: T) => void; // Add this
  updateLoading?: boolean;
}

const FeedbackForm = <T extends BaseFeedbackComponent>({
  componentId,
  recordId,
  componentStatuses,
  statusFieldName,
  defaultStatusId,
  initialData,
  onSubmit,
  onCancel,
  onChange,
  updateLoading,
}: FeedbackFormProps<T>) => {
  const [formData, setFormData] = useState<T>(initialData || ({} as T));
  const [replyTemplates, setReplyTemplates] = useState<ReplyTemplateDTO[]>([]);

  const [availablePlaceholders, setAvailablePlaceholders] = useState<ComponentPlaceholderDTO[]>([]);
  const [isPlaceholdersExpanded, setIsPlaceholdersExpanded] = useState(false);
  const [submittingAction, setSubmittingAction] = useState<'send' | 'update' | null>(null);
  const isCourse = componentId === ComponentConstants.DEDICATED_COURSES && defaultStatusId === CourseStatusConstants.STATUS_APPROVED
  const isRpl = componentId === ComponentConstants.RPL && defaultStatusId === RplStatusConstants.APPROVED
  const isShipping = componentId === ComponentConstants.DEDICATED_COURSES && defaultStatusId === CourseStatusConstants.SHIPPED

  const feedbackLabel = `${formData.member?.firstName || (formData as any).firstName} ${formData.member?.lastName || (formData as any).lastName} (${formData.member?.language?.title || (formData as any).language?.title})`;

  const { search: searchReplyTemplates, isLoading: isLoadingReplyTemplates } =
    useReplyTemplateApi();
  const { data: componentData } = useComponentById(componentId);
  const { data: memberTypesData, isLoading: isLoadingMemberTypes } = useMemberTypes();
  const { data: memberCategoriesData, isLoading: isLoadingMemberCategories } = useMemberCategories();

  const validationRules: ValidationRules = {
    [statusFieldName]: [{ required: true, message: 'Status is required' }],
    'member.memberCategoryId': [{ required: isCourse || isRpl, message: 'FCA Status is required' }],
    'member.memberTypeId': [{ required: isCourse || isRpl, message: 'Membership is required' }],
    courierTrackingReference: [{ required: isShipping, message: 'Tracking reference is required' }],
    timeMaterialShipped: [{ required: isShipping, message: 'Shipped date is required' }],
  };

  const { errors, validate } = useFormValidation(validationRules);

  // Initialize form with initialData and defaultStatusId for approve flows
  useEffect(() => {
    if (initialData) {
      // Determine status based on context:
      // 1. Approve flow: use defaultStatusId
      // 2. Drawer change status (has onSubmit but no defaultStatusId): blank
      // 3. Form (has onChange only): current status from initialData
      const statusValue = defaultStatusId
        ? defaultStatusId
        : onSubmit
          ? ''
          : initialData[statusFieldName];

      setFormData({
        ...initialData,
        [statusFieldName]: statusValue,
      } as T);
    }
  }, [initialData, defaultStatusId]);

  // Fetch component placeholders
  useEffect(() => {
    if (!componentId) {
      setAvailablePlaceholders([]);
      return;
    }

    setAvailablePlaceholders(componentData?.placeholders || []);
  }, [componentId, componentData]);

  useEffect(() => {
    const fetchReplyTemplates = async (statusId: string, compId: string) => {
      try {
        const response = await searchReplyTemplates({
          filters: {
            statusId: statusId,
            componentId: compId,
            isActive: true,
          },
          page: 1,
          pageSize: 9999,
          sortBy: 'Title',
          sortDescending: false,
        });

        setReplyTemplates(response?.data?.results || []);

        // Auto-select first reply template for approve flows
        if (defaultStatusId && response?.data?.results && response.data.results.length > 0) {
          const firstTemplate = response.data.results[0] as ReplyTemplateDTO;
          updateFormData(
            (prev) =>
              ({
                ...prev,
                replyTemplateId: firstTemplate?.id,
                feedback: firstTemplate?.body || '',
              }) as T,
          );
        }
      } catch (error) {
        console.error('Error fetching reply templates:', error);
      }
    };

    if (formData[statusFieldName] && componentId) {
      fetchReplyTemplates(formData[statusFieldName] as string, componentId);
    } else {
      setReplyTemplates([]);
    }
  }, [formData[statusFieldName], componentId]);

  useEffect(() => {
    if (formData.replyTemplateId) {
      const replyTemplate = replyTemplates.find((t) => t.id === formData.replyTemplateId);
      if (replyTemplate) {
        updateFormData(
          (prev) =>
            ({
              ...prev,
              replyTemplateId: replyTemplate.id,
              feedback: replyTemplate.body || '',
            }) as T,
        );
      }
    }
  }, [formData.replyTemplateId]);

  // Update the setFormData calls to notify parent
  const updateFormData = (updater: React.SetStateAction<T>) => {
    setFormData((prev) => {
      const newData = typeof updater === 'function' ? updater(prev) : updater;
      onChange?.(newData); // Notify parent of changes
      return newData;
    });
  };

  const handleSubmit = (sendFeedbackEmail: boolean) => {
    if (onSubmit && validate(formData)) {
      setSubmittingAction(sendFeedbackEmail ? 'send' : 'update');
      onSubmit({ ...formData, sendFeedbackEmail } as T);
    }
  };

  // Reset submitting state when updateLoading changes
  useEffect(() => {
    if (!updateLoading) {
      setSubmittingAction(null);
    }
  }, [updateLoading]);

  const courseTitle = (formData as any)?.courseType?.title || '';
  const courseTitleSuffix = courseTitle ? ` (Course: ${courseTitle})` : '';

  return (
    <>
      <FormField
        id={statusFieldName as string}
        label="Status"
        type="dropdown"
        value={(formData[statusFieldName] as string) || ''}
        onChange={(e) => handleChange(e, updateFormData)}
        placeholder="Select Status"
        required
        error={errors[statusFieldName]}
        options={componentStatuses.map((status) => ({
          value: status.value || '',
          label: status.label || '',
        }))}
      />
      {(isCourse || isRpl) && (
        <>
          <FormField
            id="memberCategoryId"
            label={`FCA Status ${courseTitleSuffix}`}
            type="dropdown"
            value={formData.member?.memberCategoryId || ''}
            onChange={(e) => {
              updateFormData((prev) => ({
                ...prev,
                member: {
                  ...prev.member,
                  memberCategoryId: e.target.value,
                } as MemberDTO,
              }));
            }}
            placeholder="Select FCA Status"
            required={true}
            error={errors['member.memberCategoryId']}
            options={(memberCategoriesData || []).map((status) => ({
              value: status.id,
              label: status.title,
            }))}
            isLoading={isLoadingMemberCategories}
          />
          <FormField
            id="memberTypeId"
            label={`Membership${courseTitleSuffix}`}
            type="dropdown"
            value={formData.member?.memberTypeId || ''}
            onChange={(e) => {
              updateFormData((prev) => ({
                ...prev,
                member: {
                  ...prev.member,
                  memberTypeId: e.target.value,
                } as MemberDTO,
              }));
            }}
            placeholder="Select FCA Status"
            required={true}
            error={errors['member.memberTypeId']}
            options={(memberTypesData || []).map((status) => ({
              value: status.id,
              label: status.title,
            }))}
            isLoading={isLoadingMemberTypes}
          />
          <FormField
            id="dateCertificateHunter"
            label="Certificate Date (Hunter)"
            type="date"
            value={formatDateForInput(formData.member?.dateCertificateHunter)}
            onChange={(e) => {
              updateFormData((prev) => ({
                ...prev,
                member: {
                  ...prev.member,
                  dateCertificateHunter: e.target.value ? new Date(e.target.value) : undefined,
                } as MemberDTO,
              }));
            }}
            placeholder="Select FCA Status"
            showTodayLink={true}
            required={false}
            allowFutureDates={false}
          />
          <FormField
            id="dateCertificateSport"
            label="Certificate Date (Sport)"
            type="date"
            value={formatDateForInput(formData.member?.dateCertificateSport)}
            onChange={(e) => {
              updateFormData((prev) => ({
                ...prev,
                member: {
                  ...prev.member,
                  dateCertificateSport: e.target.value ? new Date(e.target.value) : undefined,
                } as MemberDTO,
              }));
            }}
            placeholder="Select FCA Status"
            showTodayLink={true}
            required={false}
            allowFutureDates={false}
          />
        </>
      )}

      {isShipping && (
        <>
          <FormField
            id="courierTrackingReference"
            label="Tracking Reference"
            type="text"
            value={(formData as any).courierTrackingReference || ''}
            onChange={(e) => {
              updateFormData((prev) => ({
                ...prev,
                courierTrackingReference: e.target.value,
              } as T));
            }}
            placeholder="Enter tracking reference"
            required={true}
            error={errors.courierTrackingReference}
          />
          <FormField
            id="timeMaterialShipped"
            label="Shipped Date"
            type="datetime-local"
            value={formatDateTimeForInput((formData as any).timeMaterialShipped)}
            onChange={(e) => {
              updateFormData((prev) => ({
                ...prev,
                timeMaterialShipped: e.target.value ? new Date(e.target.value) : undefined,
              } as T));
            }}
            placeholder="Select shipped date"
            showTodayLink={true}
            required={true}
            allowFutureDates={false}
            error={errors.timeMaterialShipped}
          />
        </>
      )}

      {(!isCourse && !isRpl) && (
        <>
          <FormField
            id="replyTemplateId"
            label="Feedback Template"
            type="dropdown"
            value={formData.replyTemplateId || ''}
            onChange={(e) => handleChange(e, updateFormData)} // Changed from setFormData
            placeholder="Select a template"
            error={errors.replyTemplateId}
            options={replyTemplates.map((template) => ({
              value: template.id || '',
              label: template.title || '',
            }))}
            isLoading={isLoadingReplyTemplates}
          />

          <div className='col-span-full '>
            <FormField
              id="feedback"
              label={`Feedback: ${feedbackLabel}`}
              type="richtext"
              value={formData.feedback}
              onChange={(e) => handleChange(e, updateFormData)} // Changed from setFormData
              placeholder={
                formData.replyTemplateId
                  ? 'Edit template or provide custom feedback'
                  : 'Enter your feedback'
              }
              required={false}
              error={errors.feedback}
            />

            {availablePlaceholders.length > 0 && (
              <div className="mb-6">
                <button
                  type="button"
                  onClick={() => setIsPlaceholdersExpanded(!isPlaceholdersExpanded)}
                  className="flex items-center justify-between w-full px-4 py-3 text-left bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center space-x-3">
                    <svg
                      className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${isPlaceholdersExpanded ? 'transform rotate-90' : ''
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                      Available Placeholders
                    </h3>
                    <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded-full">
                      {availablePlaceholders.length}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {isPlaceholdersExpanded ? 'Click to collapse' : 'Click to expand'}
                  </span>
                </button>

                {isPlaceholdersExpanded && (
                  <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <Table
                      columns={[
                        {
                          label: 'Placeholder',
                          key: 'tag',
                          render: (row: ComponentPlaceholderDTO) => (
                            <code className="px-2 py-1 text-sm font-mono bg-primary-50 dark:bg-primary-900 text-primary-600 dark:text-primary-300 rounded">
                              {row.tag}
                            </code>
                          ),
                        },
                        { label: 'Description', key: 'description' },
                      ]}
                      data={availablePlaceholders}
                      allowPaging={false}
                      isLoading={false}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {onSubmit && onCancel && (
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => handleSubmit(true)}
            disabled={updateLoading}
          >
            {updateLoading && submittingAction === 'send' ? 'Submitting...' : 'Update & Send'}
          </Button>
          {!isRpl && !isShipping && (
            <Button
              type="button"
              variant="primary"
              onClick={() => handleSubmit(false)}
              disabled={updateLoading}
            >
              {updateLoading && submittingAction === 'update' ? 'Submitting...' : 'Update'}
            </Button>
          )}
        </div>
      )}
    </>
  );
};

export default FeedbackForm;
